import React, { useEffect, useState } from "react";
import { ExternalLink, Github, X } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion, AnimatePresence } from "framer-motion";

const headerVariants = {
  hidden: { opacity: 0, scale: 0.8, x: 50 }, // Start offscreen to the right
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    x: 0, // Moves to its final position
    transition: { duration: 1, ease: "easeOut" },
  },
};

const projects = [
  {
    title: "E-commerce Platform",
    description:
      "A full-featured e-commerce platform built with React, Node.js, and MongoDB.",
    image: "/hw.png",
    extraMessage: `
    ✅ Product Catalog – Browse a wide variety of hemp bags with detailed descriptions, high-quality images, and pricing.
    ✅ Shopping Cart & Checkout – Add products to the cart, view total prices, and complete the purchase with secure payment options.
    ✅ Product Reviews & Ratings – Customers can rate and review bags they have purchased.
    ✅ Inventory Management – Real-time updates on stock levels, ensuring products are in stock.
    ✅ Order Tracking – Customers can track their order from purchase to delivery.
    ✅ Wishlist – Users can save products to a wishlist for later purchase.
    `,
    technologies: ["React", "Node.js", "MongoDB", "Redux"],
    liveUrl: "https://example.com",
    githubUrl: "https://github.com",
  },
  {
    title: "Salon Managment System",
    description:
      "A collaborative task management application with real-time updates.",
    extraMessage: `
  ✅ **Multi-Role Access Control** – Separate dashboards for **Admin, Vendors, Clients, and Staff** with customized permissions.\n
✅ **Appointment Scheduling** – Clients can book appointments, receive reminders, and manage bookings.\n
✅ **Inventory & Service Management** – Manage salon services, products, and inventory efficiently.\n
✅ **Real-Time Updates** – Get instant updates on bookings, cancellations, and staff schedules.\n
✅ **Reviews & Ratings** – Clients can leave reviews and rate services.\n\n
    `,
    image: "/sms.png",
    technologies: ["React", "Next", "MongoDB", "NodeJs"],
    liveUrl: "https://example.com",
    githubUrl: "https://github.com",
  },
  {
    title: "Mobile House Listing Platform",
    description:
      "Platform where simplifies the process of finding the perfect mobile home",
    extraMessage: `
      🛠 **Software Architecture & Design Patterns:**.
      ✅ **SOLID Principles** – Ensures maintainable, scalable, and flexible code structure.\n
      ✅ **Repository Pattern** – Separates data access logic to improve code reusability and scalability.\n
      `,
    image: "/sh2.png",
    technologies: ["React", "Laravel", "MySQL"],
    liveUrl: "https://example.com",
    githubUrl: "https://github.com",
  },
  {
    title: "Weather Forecasting",
    description:
      "A weather application showing forecasts and historical data visualization.",
    image: "/weather-forecasting.png",
    technologies: ["React", "OpenWeather API"],
    extraMessage: ``,
    liveUrl: "https://example.com",
    githubUrl: "https://github.com/nishansuwal/weather",
  },
];

function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  let formattedMessage = [];
  if (selectedProject) {
    formattedMessage = selectedProject.extraMessage
      .split(".")
      .filter((item) => item.trim() !== "");
  }

  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: false,
      offset: 200,
    });
  }, []);

  return (
    <section id="projects" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4">
        <motion.div
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.5 }}
        >
          <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-600 to-teal-400 bg-clip-text text-transparent">
            Featured Projects
          </h2>
        </motion.div>

        {/* <motion.div
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.5 }}
        > */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl hover:shadow-xl transition-shadow"
              onClick={() => setSelectedProject(project)}
            >
              <div className="relative group">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-4">
                  <div className="flex gap-4">
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-blue-400 transition-colors"
                      onClick={(e) => e.stopPropagation()} // Prevents modal from opening
                    >
                      <ExternalLink className="w-6 h-6" />
                    </a>
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-blue-400 transition-colors"
                      onClick={(e) => e.stopPropagation()} // Prevents modal from opening
                    >
                      <Github className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3
                  className="text-xl font-semibold mb-2 cursor-pointer hover:text-blue-600"
                  onClick={() => setSelectedProject(project)}
                >
                  {project.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* </motion.div> */}
      </div>

      {/* Modal Popup */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-lg w-11/12 md:w-2/3 lg:w-1/2 max-h-[80vh] p-6 overflow-y-auto"
        initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-2xl font-bold">{selectedProject.title}</h3>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-48 object-cover rounded-lg mt-4"
              />

              <p className="mt-4 text-gray-600 dark:text-gray-300">
                {selectedProject.description}
              </p>

              <p className="mt-2 text-blue-500 font-semibold">
                🚀 **New Technologies & Features Used:**
              </p>
              {/* <p className="mt-2 text-blue-500 font-semibold">
                {selectedProject.extraMessage}
              </p> */}
              <p className="mt-2 text-blue-500 font-semibold">
                {formattedMessage.map((sentence, index) => (
                  <span key={index}>
                    {sentence.trim()}.
                    <br />{" "}
                    {/* This will ensure each sentence starts from a new line */}
                  </span>
                ))}
              </p>

              {/* <div className="flex gap-4 mt-4">
                <a
                  href={selectedProject.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <ExternalLink className="w-5 h-5" />
                  Live Demo
                </a>
                <a
                  href={selectedProject.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition"
                >
                  <Github className="w-5 h-5" />
                  GitHub Repo
                </a>
              </div> */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default Projects;
